/**
 * Yay geometrisi — `components/gauge-arc.tsx` için saf hesap.
 *
 * Açı birimi: **derece, saat 12'den saat yönünde**. Gösterge okuma yönü bu;
 * SVG'nin kendi 0°'ı (saat 3) ile uğraşmak her çağrı yerinde 90° çıkarmak
 * demekti. Dönüşüm tek yerde, `polar()` içinde.
 */

export type Point = { x: number; y: number };

/** Saat 12'den saat yönünde `deg` derecedeki nokta. */
export function polar(cx: number, cy: number, r: number, deg: number): Point {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(a), y: cy - r * Math.cos(a) };
}

/** `a0` → `a1` yayının `d` niteliği. Yön işaretten geliyor, sarma yok. */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p0 = polar(cx, cy, r, a0);
  const p1 = polar(cx, cy, r, a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  const sweep = a1 >= a0 ? 1 : 0;
  return `M ${p0.x.toFixed(3)} ${p0.y.toFixed(3)} A ${r} ${r} 0 ${large} ${sweep} ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`;
}

/**
 * Bir uzunluğun (px değil, yarıçapla aynı birimde) yay üzerinde karşılık
 * geldiği açı. Boşluklar ve topuz çapı çizgisel ölçüldüğü için gerekli:
 * "7 birimlik boşluk" yarıçap değişince aynı açıyı vermiyor.
 */
export function lengthToDeg(r: number, len: number): number {
  return (len / r) * (180 / Math.PI);
}

export type Box = { x: number; y: number; w: number; h: number };

/**
 * Çizilen her şeyi içine alan kutu.
 *
 * Yayın uç noktalarına bakmak YETMEZ: yay 0° ya da ±90°'yi geçiyorsa en uç
 * nokta ortada kalır. Uçlar ve geçilen dörtte-bir noktaları birlikte
 * değerlendiriliyor; `extras` topuz gibi yayın dışına taşan daireler için.
 */
export function arcBox(
  cx: number,
  cy: number,
  rOuter: number,
  a0: number,
  a1: number,
  extras: { x: number; y: number; r: number }[] = [],
): Box {
  const lo = Math.min(a0, a1);
  const hi = Math.max(a0, a1);
  const angles = [lo, hi];
  for (let q = Math.ceil(lo / 90) * 90; q <= hi; q += 90) angles.push(q);

  const pts = angles.map((a) => polar(cx, cy, rOuter, a));
  let x0 = Math.min(...pts.map((p) => p.x));
  let x1 = Math.max(...pts.map((p) => p.x));
  let y0 = Math.min(...pts.map((p) => p.y));
  let y1 = Math.max(...pts.map((p) => p.y));

  for (const e of extras) {
    x0 = Math.min(x0, e.x - e.r);
    x1 = Math.max(x1, e.x + e.r);
    y0 = Math.min(y0, e.y - e.r);
    y1 = Math.max(y1, e.y + e.r);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
